import { Head, router } from "@inertiajs/react"
import { Hourglass, MailCheck } from "lucide-react"
import { useTranslation } from "react-i18next"

import { EmptyState } from "@/components/empty-state"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { useOnline } from "@/hooks/use-online"
import { sessionPath } from "@/routes"
import type { PendingIndexProps } from "@/types/collab"

/**
 * Pending/Index — S1 standalone page for users with no role / pending invite.
 *
 * D3 decision: no shell, no nav. The user has not yet been granted any
 * tenancy, so every piece of brand chrome is a lie. Strip the UI to:
 *   - logo area (handled by centered EmptyState → Inbox/Hourglass icon)
 *   - heading + empathetic body copy
 *   - "Log out" button (primary action)
 *   - offline Alert + disabled logout when navigator.onLine is false
 *
 * Backend keeps the prop contract stable so cycle-2 invite work can swap
 * `inviter_brand` from null to a real object without touching this page.
 *
 * The page does NOT mount inside `AppLayout` — it renders directly under
 * `<PersistentLayout>` so no CreatorShell/BrandShell/AdminShell wraps it.
 */
export default function PendingIndex({
  session_id,
  reason,
}: PendingIndexProps) {
  const { t } = useTranslation()

  // Online/offline state lives in the shared `useOnline` hook — same
  // SSR-safe lazy init + subscribe pattern, now reused across the admin
  // console pages (OKS-15) and `Invitations/Accept`.
  const online = useOnline()

  function handleSignOut() {
    if (!session_id || !online) return
    router.delete(sessionPath({ id: session_id }))
  }

  // Reason drives both the icon and the copy bucket. `no_role` is the V1
  // dominant case — zero-tenancy non-admin; `pending_invite` is reserved
  // for cycle-2's invite tracking.
  const Icon = reason === "pending_invite" ? MailCheck : Hourglass
  const titleKey = `pending_page.${reason}.title`
  const descriptionKey = `pending_page.${reason}.description`

  const logoutDisabled = !session_id || !online

  return (
    <>
      <Head title={t(titleKey)} />
      <main
        className="bg-background py-safe flex min-h-screen items-center justify-center px-4"
        tabIndex={-1}
        data-testid="pending-page"
      >
        <div className="flex w-full max-w-md flex-col gap-4">
          {!online && (
            <Alert data-testid="pending-offline-alert">
              <AlertDescription>
                {t("pending_page.offline_banner")}
              </AlertDescription>
            </Alert>
          )}
          <EmptyState
            icon={Icon}
            title={t(titleKey)}
            description={t(descriptionKey)}
            actions={
              session_id ? (
                <Button
                  variant="outline"
                  onClick={handleSignOut}
                  disabled={logoutDisabled}
                  data-testid="pending-logout"
                  aria-disabled={logoutDisabled ? "true" : undefined}
                >
                  {t("pending_page.logout")}
                </Button>
              ) : null
            }
          />
        </div>
      </main>
    </>
  )
}
